import { Base } from '../../abstract/base.model';
import { nullableDecimal, nullableNumber, nullableString } from '../utils/wire-value-utils';
import { isRecord } from '../utils/is-record';
import { MarketplacePaymentStatus } from './marketplace-payment-status.model';

/**
 * A marketplace payment as `/admin/payments` returns it (hub `admin-payments-controller.ts`), built to the agreed
 * `ADMIN_PAYMENT_READABLE` list. Stripe transfer / account / fee ids, the approval audit fields and raw Stripe
 * objects are deliberately not mapped.
 *
 * Every field is read-only here: payments change only through the approve / adjust / decline / release actions.
 * Fields the hub didn't send stay `undefined` (an explicit `null` stays `null`); display fallbacks belong in templates.
 * Amounts are dollars (the hub's decimal columns may arrive as numeric strings). Lifecycle timestamps stay ISO strings.
 */
export class MarketplacePayment extends Base {
  jobId?: string | null;
  bidId?: string | null;
  farmerOrgId?: string | null;
  pilotOrgId?: string | null;
  status?: MarketplacePaymentStatus | null;
  currency?: string | null;
  billableAcres?: number | null;
  pricePerAcre?: number | null;
  grossAmount?: number | null;
  totalChargeAmount?: number | null;
  farmerSurchargeAmount?: number | null;
  pilotFeeDeductionAmount?: number | null;
  pilotPayoutAmount?: number | null;
  originalAgreedAmount?: number | null;
  refundAmount?: number | null;
  /** Static text only (hub rule). */
  failureReason?: string | null;
  acceptanceAttemptCount?: number | null;
  stripePaymentIntentId?: string | null;
  adminApprovalRequiredAt?: string | null;
  capturedAt?: string | null;
  payoutEligibleAt?: string | null;
  transferredAt?: string | null;

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    this.readIdentity(source);
    this.readAmounts(source);
    this.readLifecycle(source);
  }

  private readIdentity(source: Record<string, unknown>): void {
    this.jobId = nullableString(source['jobId']);
    this.bidId = nullableString(source['bidId']);
    this.farmerOrgId = nullableString(source['farmerOrgId']);
    this.pilotOrgId = nullableString(source['pilotOrgId']);
    this.status = nullableString(source['status']) as MarketplacePaymentStatus | null | undefined;
    this.currency = nullableString(source['currency']);
    this.stripePaymentIntentId = nullableString(source['stripePaymentIntentId']);
  }

  private readAmounts(source: Record<string, unknown>): void {
    this.billableAcres = nullableDecimal(source['billableAcres']);
    this.pricePerAcre = nullableDecimal(source['pricePerAcre']);
    this.grossAmount = nullableDecimal(source['grossAmount']);
    this.totalChargeAmount = nullableDecimal(source['totalChargeAmount']);
    this.farmerSurchargeAmount = nullableDecimal(source['farmerSurchargeAmount']);
    this.pilotFeeDeductionAmount = nullableDecimal(source['pilotFeeDeductionAmount']);
    this.pilotPayoutAmount = nullableDecimal(source['pilotPayoutAmount']);
    this.originalAgreedAmount = nullableDecimal(source['originalAgreedAmount']);
    this.refundAmount = nullableDecimal(source['refundAmount']);
  }

  private readLifecycle(source: Record<string, unknown>): void {
    this.failureReason = nullableString(source['failureReason']);
    this.acceptanceAttemptCount = nullableNumber(source['acceptanceAttemptCount']);
    this.adminApprovalRequiredAt = nullableString(source['adminApprovalRequiredAt']);
    this.capturedAt = nullableString(source['capturedAt']);
    this.payoutEligibleAt = nullableString(source['payoutEligibleAt']);
    this.transferredAt = nullableString(source['transferredAt']);
  }
}
